<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto" readonly
                :model-value="tanaoroshi.startDatetime"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.startDatetime')" />
            </v-col>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto" readonly
                :model-value="tanaoroshi.endDatetime"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.endDatetime')" />
            </v-col>
            <v-col cols="3">
              <v-text-field density="compact" variant="outlined" hide-details="auto" readonly
                :model-value="tanaoroshi.kakuteisyaCode"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.kakuteisyaCode')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                :items="tanaoroshiItems"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.tanaoroshi')"
                item-title="title"
                item-value="tanaoroshiId"
                v-model="tanaoroshiId"
                @update:model-value="changeTanaoroshi"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                :items="shopItems"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.shop')"
                item-title="title"
                item-value="zoneKey"
                v-model="targetZoneKey"
                @update:model-value="resetPage()"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                :items="moveTypeItems"
                :label="$t('tanaoroshiWarehouseTransferDetail.label.moveType')"
                item-title="title"
                item-value="moveType"
                v-model="targetMoveType"
                @update:model-value="resetPage()"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap resizable-table">
                <thead>
                  <tr>
                    <th v-for="header in headers" :key="header">
                      <div class="header-title">
                        {{ $t("tanaoroshiWarehouseTransferDetail.label.table." + header) }}
                      </div>
                      <div class="resize-handle" @mousedown="resizeColumnWidth"></div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in pagedDataList" :key="data.no">
                    <td v-for="header in headers" :key="header">{{ data[header] }}</td>
                  </tr>
                  <tr>
                    <td :colspan="headers.length"></td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="currentPageNo" :length="maxPageNo" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue="resetPage()"
                    return-object />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayContentNo.minNo }} - {{ displayContentNo.maxNo }}{{ $t('common.label.displayedDataRange') }} / {{ filteredDataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-8 bg-indigo-darken-1 mr-5"
                elevation="2"
                @click="downloadCsv"
                :disabled="filteredDataList.length == 0">{{ $t('common.action.outputCsv') }}
              </v-btn>
              <v-btn
                theme="dark"
                class="px-8 bg-grey-darken-3"
                elevation="2"
                @click="back">{{ $t('common.action.back') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
        <ErrorDialog v-if="dataError" :URL="previousURL" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import ErrorDialog from "@/components/dialog/ErrorDialog";
import api from "@/apis/staff";
import utils from "@/utils";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import tableUtils from "@/utils/tableUtils";

// 全店・全種別を表す値
const ALL_ZONE_KEY = "";
const ALL_MOVE_TYPE = 0;
// 移動種別（表示順）
const MOVE_TYPE_LIST = [1, 2, 3, 4];

/**
 * 棚卸倉庫移動明細
 * 本社の棚卸確定時に移動した資産の一覧（1行＝1資産、移動先側の明細）。
 * 棚卸回・店舗（移動先ロケ）・移動種別で絞り込み、表示中のレコードをCSVで出力できる（プロプラス取込用）。
 * （JCIKAIHATSU-339 資産管理 #23・#24）
 */
export default {
  components: { AppWrapperView, ErrorDialog },
  computed: {
    headers() {
      return this.$getTableDef("TanaoroshiWarehouseTransferDetailPage");
    },
    csvHeaders() {
      return this.$getTableDef("TanaoroshiWarehouseTransferDetailCsv");
    },
    // 棚卸回の選択肢（確定済みの本社棚卸）
    tanaoroshiItems() {
      return this.tanaoroshiList.map((x) => ({
        tanaoroshiId: x.tanaoroshiId,
        title: x.tanaoroshiId + "（" + utils.putSplitWordToCalendarWithTime(x.endDatetime) + "）",
      }));
    },
    // 店舗の選択肢（移動先ロケ単位。本社の各店舗はロケとして管理されているため。先頭は全店）
    shopItems() {
      const items = [];
      const seen = new Set();
      this.dataList.forEach((x) => {
        const zoneKey = this.toZoneKey(x);
        if (seen.has(zoneKey)) return;
        seen.add(zoneKey);
        items.push({
          zoneKey: zoneKey,
          siteCode: x.destinationSiteCode,
          zoneCode: x.destinationZoneCode,
          zoneOrder: x.destinationZoneOrder ?? Number.MAX_SAFE_INTEGER,
          title: x.destinationZoneName ?? x.destinationZoneCode,
        });
      });
      // 店舗コード → ロケの表示順 → ロケコードの順
      items.sort((a, b) => a.siteCode.localeCompare(b.siteCode) || a.zoneOrder - b.zoneOrder || a.zoneCode.localeCompare(b.zoneCode));
      return [{ zoneKey: ALL_ZONE_KEY, title: this.$t("tanaoroshiWarehouseTransferDetail.label.allShop") }, ...items];
    },
    // 移動種別の選択肢（先頭は全種別）
    moveTypeItems() {
      return [ALL_MOVE_TYPE, ...MOVE_TYPE_LIST].map((x) => ({
        moveType: x,
        title: this.$t("tanaoroshiWarehouseTransfer.moveType." + x),
      }));
    },
    // 表示用に変換したデータ
    displayDataList() {
      return this.dataList.map((x) => ({
        ...x,
        tanaoroshiId: this.tanaoroshi.tanaoroshiId,
        endDatetime: this.tanaoroshi.endDatetime,
        moveTypeLabel: this.$t("tanaoroshiWarehouseTransfer.moveType." + x.moveType),
        judgeLabel: x.judgeType ? this.$t("tanaoroshiWarehouseTransferDetail.judgeType." + x.judgeType) : "",
        // 移動元が一意に決まらない場合は候補ロケを表示する
        originZoneName: x.originZoneName
          || (x.originCandidateName ? this.$t("tanaoroshiWarehouseTransferDetail.label.originCandidate", [x.originCandidateName]) : ""),
      }));
    },
    // 店舗（移動先ロケ）・移動種別で絞り込んだデータ（Noは絞り込み後に振り直す）
    filteredDataList() {
      return this.displayDataList
        .filter((x) => this.targetZoneKey == ALL_ZONE_KEY || this.toZoneKey(x) == this.targetZoneKey)
        .filter((x) => this.targetMoveType == ALL_MOVE_TYPE || x.moveType == this.targetMoveType)
        .map((x, index) => ({ ...x, no: index + 1 }));
    },
    displayContentNo() {
      const total = this.filteredDataList.length;
      const minNo = total != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * this.currentPageNo;
      if (maxNo > total || this.displayPerPage.value == 0) {
        return { minNo: minNo, maxNo: total };
      }
      return { minNo: minNo, maxNo: maxNo };
    },
    maxPageNo() {
      if (this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.filteredDataList.length / this.displayPerPage.value);
    },
    pagedDataList() {
      return this.filteredDataList.slice(this.displayContentNo.minNo - 1, this.displayContentNo.maxNo);
    },
  },

  data() {
    return {
      tanaoroshi: {},
      tanaoroshiList: [],
      tanaoroshiId: null,
      dataList: [],
      targetZoneKey: ALL_ZONE_KEY,
      targetMoveType: ALL_MOVE_TYPE,
      currentPageNo: 1,
      displayPerPage: {},
      displayPerPageItem: [],
      dataError: false,
      previousURL: Paths.TANAOROSHI_WAREHOUSE_TRANSFER,
    };
  },
  methods: {
    resetPage() {
      this.currentPageNo = 1;
    },
    // 移動先ロケを識別するキー（店舗コード＋ロケコード）
    toZoneKey(x) {
      return x.destinationSiteCode + "_" + x.destinationZoneCode;
    },
    back() {
      router.push({ name: Paths.TANAOROSHI_WAREHOUSE_TRANSFER });
    },
    changeTanaoroshi(tanaoroshiId) {
      this.resetPage();
      this.fetchDetail(tanaoroshiId);
    },
    // 表示中（店舗・移動種別で絞り込んだ全ページ分）のレコードをCSVで出力する
    downloadCsv() {
      // 機械名称にカンマを含む場合があるため、ダブルクォートで囲む版を使用する
      utils.quotedSimpleDownloadCsv(this.csvHeaders, this.filteredDataList,
        "tanaoroshiWarehouseTransferDetail.label.table.",
        "tanaoroshiWarehouseTransferDetail.fileNamePrefix.csv", this.$t);
    },
    // 棚卸回の選択肢（確定済みの本社棚卸）を取得する
    fetchTanaoroshiList() {
      api.post("/tanaoroshi/warehouse-transfer/search", {})
        .then((response) => {
          this.tanaoroshiList = response.data.results?.tanaoroshiList ?? [];
        });
    },
    fetchDetail(tanaoroshiId) {
      api.post("/tanaoroshi/warehouse-transfer/detail/search", {
        tanaoroshiId: tanaoroshiId,
      })
        .then((response) => {
          if (response.data.status != 0 || !response.data.results?.tanaoroshi) {
            this.dataError = true;
            return;
          }
          const results = response.data.results;
          this.tanaoroshi = {
            ...results.tanaoroshi,
            startDatetime: utils.putSplitWordToCalendarWithTime(results.tanaoroshi.startDatetime),
            endDatetime: utils.putSplitWordToCalendarWithTime(results.tanaoroshi.endDatetime),
          };
          this.dataList = results.detailList ?? [];
          // 選択中の店舗（移動先ロケ）が明細に無い場合は全店を表示する
          if (!this.shopItems.some((x) => x.zoneKey == this.targetZoneKey)) {
            this.targetZoneKey = ALL_ZONE_KEY;
          }
        })
        .catch(() => {
          this.dataError = true;
        });
    },
    resizeColumnWidth: tableUtils.resizeColumnWidth,
  },

  mounted() {
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);
    this.displayPerPage = this.displayPerPageItem[0];

    const tanaoroshiId = Number(this.$route.query.tanaoroshiId);
    if (!tanaoroshiId) {
      this.dataError = true;
      return;
    }
    const moveType = Number(this.$route.query.moveType);
    this.targetMoveType = MOVE_TYPE_LIST.includes(moveType) ? moveType : ALL_MOVE_TYPE;
    this.tanaoroshiId = tanaoroshiId;
    this.fetchTanaoroshiList();
    this.fetchDetail(tanaoroshiId);
  },
};
</script>
